import React, { ReactNode } from 'react';
import { XIcon } from '@heroicons/react/solid';
import { useDispatch } from 'react-redux';
import { removeNotification } from '../../redux/actions/ui/removeNotification';
import styles from './BasicNotification.module.css'
import CheckCircleLineIcon from '../../componentsNew/atoms/icons/CheckCircleLineIcon';
import ErrorWarningLineIcon from '../../componentsNew/atoms/icons/ErrorWarningLineIcon';
import AlertLineIcon from '../../componentsNew/atoms/icons/AlertLineIcon';
import InformationLineIcon from '../../componentsNew/atoms/icons/InformationLineIcon';
import Text from '../../componentsNew/atoms/typography/Text';
import { FontSizeEnum, FontWeightEnum } from '../../componentsNew/enums';

export enum NotificationTypes {
    SUCCESS = 'success',
    FAILED = 'failed',
    INFO = 'info',
    WARNING = 'warning',
    FAILED_DARK = 'failed_dark',
}

interface BasicNotificationProps {
    id: string;
    type: NotificationTypes;
    heading: ReactNode;
    message: ReactNode;
}

const BasicNotification = ({
    id,
    type = NotificationTypes.FAILED,
    heading = null,
    message,
}: BasicNotificationProps) => {
    const dispatch = useDispatch();

    setTimeout(() => {
        dispatch(removeNotification(id));
    }, 8000);

    const getClassname = (type) => {
        switch (type) {
            case NotificationTypes.FAILED_DARK:
                return styles.notificationFailedDark;
            case NotificationTypes.FAILED:
                return styles.notificationFailed;
            case NotificationTypes.INFO:
                return styles.notificationInfo;
            case NotificationTypes.WARNING:
                return styles.notificationWarning;
            case NotificationTypes.SUCCESS:
                return styles.notificationSuccess;
        }
    }

    const getCloseButtonClassNames = (type) => {
        switch (type) {
            case NotificationTypes.FAILED_DARK:
                return styles.closeButtonFailedDark;
            case NotificationTypes.FAILED:
                return styles.closeButtonFailed;
            case NotificationTypes.SUCCESS:
                return styles.closeButtonSuccess;
        }
    }

    const getIconByType = (type) => {
        switch (type) {
            case NotificationTypes.SUCCESS:
                return <CheckCircleLineIcon className={styles.iconSuccess}/>
            case NotificationTypes.FAILED:
                return <ErrorWarningLineIcon className={styles.iconFailed}/>
            case NotificationTypes.FAILED_DARK:
                return <ErrorWarningLineIcon className={styles.iconFailedDark}/>
            case NotificationTypes.WARNING:
                return <AlertLineIcon className={styles.iconWarning}/>
            case NotificationTypes.INFO:
                return <InformationLineIcon className={styles.iconInfo}/>
        }
    }

    return (
        <div
            className={styles.basicNotification}
        >
            <div
                className={`${styles.notification} ${getClassname(type)}`}
            >
                <div className={styles.notificationContent}>
                    <div className={styles.iconContainer}>
                        {getIconByType(type)}
                    </div>
                    <div className={styles.notificationContainer}>
                        {heading && 
                            <Text
                                tag='p'
                                weight={FontWeightEnum.Bold}
                                size={FontSizeEnum.Md}
                            >
                                {heading}
                            </Text>
                        }
                        <div className={styles.messageContainer}>
                            <p className={styles.message}>{message}</p>
                        </div>
                    </div>
                    <div className={styles.closeButtonContainer}>
                        <button
                            type="button"
                            className={`${styles.closeButton} ${getCloseButtonClassNames(type)}`}
                            onClick={() => dispatch(removeNotification(id))}
                        >
                            <span className="sr-only">Close</span>
                            <XIcon className={styles.closeIcon} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BasicNotification;
